import {translate} from '@docusaurus/Translate';
import type {
  FeedbackRating,
  FeedbackReason,
  QualityReason,
  QualityStatus,
  TokenUsageResponse,
} from '../../services/agent-api';

export const CONVERSATION_STORAGE_KEY = 'folderspan.ai-docs-assistant.v1';
export const CONVERSATION_STORAGE_VERSION = 1;
export const MAX_QUESTION_LENGTH = 4000;

export type PageContext = {
  title: string;
  path: string;
};

export type MessageFeedback = {
  rating: FeedbackRating;
  reason?: FeedbackReason;
  status: 'submitting' | 'submitted' | 'failed';
  error?: string;
};

export type ConversationMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  status: 'pending' | 'complete' | 'failed';
  createdAt: string;
  error?: string;
  pageContext?: PageContext;
  messageId?: string;
  qualityStatus?: QualityStatus;
  qualityReason?: QualityReason;
  recommendedQuestions?: string[];
  tokenUsage?: TokenUsageResponse;
  feedback?: MessageFeedback;
};

export type ConversationState = {
  localId: string;
  sessionId: string | null;
  messages: ConversationMessage[];
  draft: string;
  sourceContext: PageContext | null;
};

export type StoredConversation = ConversationState & {
  version: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isPageContext(value: unknown): value is PageContext {
  return (
    isRecord(value) &&
    typeof value.title === 'string' &&
    typeof value.path === 'string' &&
    value.path.startsWith('/') &&
    !value.path.startsWith('//') &&
    value.title.length <= 256 &&
    value.path.length <= 512
  );
}

export function isStoredMessage(value: unknown): value is ConversationMessage {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    (value.role === 'user' || value.role === 'assistant') &&
    typeof value.content === 'string' &&
    (value.status === 'pending' || value.status === 'complete' || value.status === 'failed') &&
    typeof value.createdAt === 'string' &&
    (value.recommendedQuestions === undefined ||
      (Array.isArray(value.recommendedQuestions) &&
        value.recommendedQuestions.length === 3 &&
        value.recommendedQuestions.every(
          (question) =>
            typeof question === 'string' &&
            question.trim().length > 0 &&
            Array.from(question).length <= 200,
        ))) &&
    (value.tokenUsage === undefined || isStoredTokenUsage(value.tokenUsage))
  );
}

function isStoredTokenUsage(value: unknown): value is TokenUsageResponse {
  if (!isRecord(value)) {
    return false;
  }

  const isOptionalCount = (count: unknown) =>
    count === undefined || count === null || (Number.isInteger(count) && Number(count) >= 0);

  return (
    isOptionalCount(value.input_tokens) &&
    isOptionalCount(value.output_tokens) &&
    isOptionalCount(value.total_tokens) &&
    isOptionalCount(value.cached_input_tokens) &&
    Number.isInteger(value.model_calls) &&
    Number(value.model_calls) >= 0 &&
    typeof value.complete === 'boolean'
  );
}

export function restoreConversationMessages(value: unknown): ConversationMessage[] {
  if (!Array.isArray(value) || !value.every(isStoredMessage)) {
    return [];
  }

  return value.flatMap((message): ConversationMessage[] => {
    if (message.status !== 'pending') {
      return [message];
    }
    if (message.role === 'assistant') {
      return [];
    }

    return [
      {
        ...message,
        status: 'failed' as const,
        error: translate({message: '页面刷新中断了这次请求，可以重新发送。'}),
      },
    ];
  });
}

export function restoreConversation(value: unknown): ConversationState | null {
  if (
    !isRecord(value) ||
    value.version !== CONVERSATION_STORAGE_VERSION ||
    (value.sessionId !== null && typeof value.sessionId !== 'string') ||
    !Array.isArray(value.messages) ||
    !value.messages.every(isStoredMessage) ||
    typeof value.draft !== 'string' ||
    (value.sourceContext !== null && !isPageContext(value.sourceContext))
  ) {
    return null;
  }

  return {
    localId: typeof value.localId === 'string' && value.localId.length > 0 ? value.localId : '',
    sessionId: typeof value.sessionId === 'string' ? value.sessionId : null,
    messages: restoreConversationMessages(value.messages),
    draft: value.draft.slice(0, MAX_QUESTION_LENGTH),
    sourceContext: isPageContext(value.sourceContext) ? value.sourceContext : null,
  };
}

export function createAssistantId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function emptyConversation(sourceContext: PageContext | null = null): ConversationState {
  return {
    localId: createAssistantId('conversation'),
    sessionId: null,
    messages: [],
    draft: '',
    sourceContext,
  };
}
